import { PersonStanding, X, Contrast, AArrowUp, Eye, Baseline, Settings2 } from "lucide-react";
import { useRef, useState, useEffect } from "react";

export const Accessibility = () => {
  const [open, setOpen] = useState(false);
  const accessibilityDialogRef = useRef(null);
  const firstFocusableRef = useRef(null);
  const lastFocusableRef = useRef(null);
  const triggerRef = useRef(null);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && open) {
      closeMenu();
    }
    
    if (e.key === 'Tab' && open) {
      const focusableElements = accessibilityDialogRef.current?.querySelectorAll(
        'button, [href], [tabindex]:not([tabindex="-1"])'
      );
      
      if (!focusableElements || focusableElements.length === 0) return;
      
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      
      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    }
  };

  const openMenu = () => {
    setOpen(true);
    // Focus first focusable element after menu opens
    setTimeout(() => {
      firstFocusableRef.current?.focus();
    }, 100);
  };

  const closeMenu = () => {
    setOpen(false);
    // Return focus to trigger button
    triggerRef.current?.focus();
  };

  const handleAccessibilityButtonClick = () => {
    if (open) {
      closeMenu();
    } else {
      openMenu();
    }
  };

  const handleAccessibilityButtonClickOnEnter = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleAccessibilityButtonClick();
    }
  };

  const increaseContrast = () => {
    document.body.classList.toggle("high-contrast");
  };

  const increaseFontSize = () => {
    document.querySelector("html").classList.toggle("large-font");
  };

  const increaseLetterSpacing = () => {
    document.querySelector("html").classList.toggle("increased-spacing");
  };

  const enableDyslexiaFont = () => {
    document.querySelector("html").classList.toggle("dyslexia-font");
  };

  const reduceMotion = () => {
    document.querySelector("html").classList.toggle("reduce-motion");
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        accessibilityDialogRef.current &&
        !accessibilityDialogRef.current.contains(event.target) &&
        !triggerRef.current?.contains(event.target)
      ) {
        closeMenu();
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  return (
    <>
      <div 
        ref={triggerRef}
        className={`accessibility-button ${open ? 'open' : ''}`} 
        role="button" 
        aria-label="Accessibility options" 
        aria-expanded={open}
        aria-haspopup="menu"
        tabIndex="0" 
        onClick={handleAccessibilityButtonClick} 
        onKeyDown={handleAccessibilityButtonClickOnEnter}
      >
        {open ? (
          <X size={26} className="icon" aria-hidden="true" />
        ) : (
          <PersonStanding size={26} className="icon" aria-hidden="true" />
        )}
      </div>
      
      <div 
        className={`accessibility-menu ${open ? 'open' : ''}`} 
        role="menu" 
        ref={accessibilityDialogRef}
        aria-labelledby="accessibility-menu-title"
      >
        <h2 id="accessibility-menu-title">Opcje dostępności</h2>
        <div className="accessibility-menu__options" role="menubar">
          <button 
            ref={firstFocusableRef}
            className="accessibility-menu__option" 
            aria-label="Zwiększ kontrast" 
            onClick={increaseContrast}
            role="menuitem"
          >
            <Contrast size={26} className="option-icon" aria-hidden="true" />
            <span>Wysoki kontrast</span>
          </button>
          
          <button 
            className="accessibility-menu__option" 
            aria-label="Zwiększ rozmiar czcionki" 
            onClick={increaseFontSize}
            role="menuitem"
          >
            <AArrowUp size={26} className="option-icon" aria-hidden="true" />
            <span>Większa czcionka</span>
          </button>
          
          <button 
            className="accessibility-menu__option" 
            aria-label="Zwiększ odstępy między literami" 
            onClick={increaseLetterSpacing}
            role="menuitem"
          >
            <Baseline size={26} className="option-icon" aria-hidden="true" />
            <span>Odstępy liter</span>
          </button>
          
          <button 
            ref={lastFocusableRef}
            className="accessibility-menu__option" 
            aria-label="Zmniejsz animacje" 
            onClick={reduceMotion}
            role="menuitem"
          >
            <Eye size={26} className="option-icon" aria-hidden="true" />
            <span>Zmniejsz ruch</span>
          </button>
        </div>
      </div>
      <style jsx>
        {`.accessibility-button {
  position: fixed;
  bottom: 10px;
  left: 10px;
  width: 50px;
  height: 50px;
  backdrop-filter: blur(7px);
  background-color: rgba(44, 133, 192, 0.6862745098);
  border: 2px solid black;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  z-index: 1000;
  transition: all 0.3s ease;
}
.accessibility-button:hover, .accessibility-button:focus {
  transform: scale(1.1);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.3);
}
.accessibility-button.open {
  background-color: #fff;
  transform: scale(1.1);
}
.accessibility-button:focus-visible {
  outline: 2px solid #0065C3;
  outline-offset: 2px;
}

.accessibility-menu {
  display: none;
  opacity: 0;
  transform: translateY(10px);
  transition: all 0.3s ease;
}
.accessibility-menu.open {
  display: block;
  opacity: 1;
  transform: translateY(0);
  position: fixed;
  bottom: 70px;
  left: 10px;
  width: 320px;
  max-height: 400px;
  overflow-y: auto;
  background-color: #fff;
  border: 2px solid black;
  border-radius: 8px;
  z-index: 1001;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
  padding: 20px;
}
.accessibility-menu h2 {
  margin: 0 0 20px;
  font-size: 1.2em;
  color: black;
  font-weight: bold;
}
.accessibility-menu__options {
  display: grid;
  gap: 10px;
}
.accessibility-menu__option {
  display: grid;
  grid-template-columns: 40px 1fr;
  align-items: center;
  border: 2px solid black;
  padding: 0;
  font-size: 1em;
  font-weight: bold;
  cursor: pointer;
  background-color: #fff;
  transition: all 0.2s ease;
  border-radius: 4px;
  overflow: hidden;
}
.accessibility-menu__option:hover, .accessibility-menu__option:focus {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  background-color: #f5f5f5;
  transform: translateY(-1px);
}
.accessibility-menu__option:focus-visible {
  outline: 2px solid #0065C3;
  outline-offset: 2px;
}
.accessibility-menu__option:active {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}
.accessibility-menu__option .option-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 8px;
  background-color: #43a1e0;
  border-right: 2px solid black;
}
.accessibility-menu__option span {
  padding: 0 12px;
  text-align: left;
}

.high-contrast .accessibility-button {
  background-color: #000;
  border-color: #fff;
  color: #fff;
}
.high-contrast .accessibility-menu {
  background-color: #000;
  border-color: #fff;
  color: #fff;
}
.high-contrast .accessibility-menu h2 {
  color: #fff;
}
.high-contrast .accessibility-menu__option {
  background-color: #000;
  border-color: #fff;
  color: #fff;
}
.high-contrast .accessibility-menu__option:hover, .high-contrast .accessibility-menu__option:focus {
  background-color: #333;
}
.high-contrast .accessibility-menu__option .option-icon {
  background-color: #333;
  border-color: #fff;
}

@media (prefers-reduced-motion: reduce) {
  .accessibility-button,
  .accessibility-menu,
  .accessibility-menu__option {
    transition: none !important;
  }
}
.reduce-motion .accessibility-button,
.reduce-motion .accessibility-menu,
.reduce-motion .accessibility-menu__option {
  transition: none !important;
}

@media (max-width: 400px) {
  .accessibility-menu {
    width: calc(100vw - 20px);
    left: 10px;
    right: 10px;
  }
}`}
      </style>
    </>
  )
}
